/* ===================================================================
   Competitors - EXACT port of the Competitors screen from the Claude
   Design prototype (Rhody Reviews.dc.html, desktop 1393-1448, mobile
   2543-2571). Corbato, 2026-07-03.

   The prototype Competitors screen is ONE scroll: a sticky header with a
   "Track a competitor" control, a 3-up position callout (rating rank,
   your rating, where to gain), a comparison table (business / rating /
   total reviews bar / new), and a blue insight callout. This component
   reproduces that layout 1:1 and binds it to LIVE data.

   DATA WIRING (unchanged behavior):
     - GET  /competitors/scorecard  -> useCompetitorScorecard()  (leaderboard
       rows = client + rivals, ranks, ratings, review counts, per-month)
     - GET  /competitors            -> useCompetitors()          (tracked list,
       carries new_in_period per rival for the "new" column + remove)
     - POST /competitors            -> add (via the search-by-name modal)
     - DELETE /competitors/{id}     -> remove
     - POST /competitors/refresh    -> refresh from Google

   HONESTY IS BAKED IN (Hard Rule: no fabricated metrics):
     - Ratings / counts / per-month come straight from the API; a missing
       value renders a dash, never an invented number.
     - "Rating rank" is computed honestly by sorting the leaderboard rows by
       avg_rating (the API leaderboard is ranked by review COUNT).
     - "Area average" is the real mean of the tracked rivals' ratings; hidden
       when no rival has a rating yet.
     - "Where to gain" is derived from the real gap-to-leader; hidden when the
       client is already leading or the field is bare.
     - The "new" column reads new_in_period from the tracked list (0 -> dash).
     - competitor_place_id is an OPAQUE key; it is NEVER rendered.

   There are no data-tour / data-keepcolor hooks in the prototype's
   Competitors markup (those live on the shell/dashboard), so none are
   carried here.
   =================================================================== */
import { useEffect, useRef, useState } from 'react';
import { Icon, ClosePath, SearchPath } from '../components/ui';
import {
  useCompetitors,
  useCompetitorScorecard,
  useCompetitorMutations,
  usePlacesSearch,
} from '../hooks';
import type { Competitor, LeaderboardRow, PlaceMatch } from '../lib/api';
import { avatarColor, initialOf } from '../lib/display';
import '../styles/components.css';
import '../styles/competitors.css';

const PlusPath = <path d="M12 5v14M5 12h14" strokeLinecap="round" />;

/* ASCII-safe glyphs (this Windows toolchain requires ASCII-only source; these
   build the real characters at runtime from their code points). */
const DASH = String.fromCharCode(0x2013);   // en dash: the "no value" cell
const STAR = String.fromCharCode(0x2605);   // filled star beside a rating
const MIDDOT = String.fromCharCode(0x00b7); // middle-dot separator
const EMDASH = String.fromCharCode(0x2014); // em dash in prose
const CHECK = String.fromCharCode(0x2713);  // toast checkmark
const InfoCircle = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
  </>
);

/* Debounce a fast-changing value (the search box). Places search bills per
   call, so we only let the query settle after the owner stops typing. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/* Owner-friendly copy for the rare search failures. We never surface raw
   error codes (invalid_query / places_unavailable / lookup_failed). */
function searchErrorMessage(err: Error): string {
  const code = (err as { detail?: string }).detail || err.message;
  if (code === 'places_unavailable') {
    return 'Business search is not available right now. Please try again in a little while.';
  }
  return 'Could not search just now. Please try again in a moment.';
}

/* ---- Add-a-competitor modal (search by name, place_id rides behind the
   scenes). Behavior preserved verbatim from the prior build. ---- */
function AddCompetitorModal({
  onClose,
  onAdd,
  saving,
  error,
}: {
  onClose: () => void;
  onAdd: (match: PlaceMatch) => void;
  saving: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const debouncedName = useDebounced(name, 350);
  const debouncedCity = useDebounced(city, 350);
  const search = usePlacesSearch(debouncedName, debouncedCity);

  const typed = name.trim().length > 0;
  const settling = typed && debouncedName.trim() !== name.trim();
  const results = search.data?.results ?? [];
  const showNoMatches =
    typed && !settling && !search.isFetching && !search.isError && results.length === 0;

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal__head">
          <h3>Add a competitor</h3>
          <button className="x" onClick={onClose} aria-label="Close"><Icon d={ClosePath} /></button>
        </div>
        <div className="modal__body">
          <div className="formgrid">
            <label className="fld full">
              <span className="lab">Business name</span>
              <div className="srch-wrap">
                <span className="srch-ico"><Icon d={SearchPath} /></span>
                <input
                  className="inp srch-inp"
                  value={name}
                  autoFocus
                  placeholder="Start typing a business name"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </label>
            <label className="fld full">
              <span className="lab">City (optional)</span>
              <input
                className="inp"
                value={city}
                placeholder="e.g. North Kingstown, RI"
                onChange={(e) => setCity(e.target.value)}
              />
            </label>
          </div>

          {typed && (
            <div className="srch-results" role="listbox" aria-label="Matching businesses">
              {(search.isFetching || settling) && results.length === 0 && (
                <p className="muted srch-note">Searching...</p>
              )}

              {search.isError && (
                <p className="srch-note" style={{ color: 'var(--red)' }}>
                  {searchErrorMessage(search.error)}
                </p>
              )}

              {results.map((m) => (
                <button
                  key={m.place_id}
                  className="srch-row"
                  role="option"
                  aria-selected="false"
                  disabled={saving}
                  onClick={() => onAdd(m)}
                >
                  <span className="srch-row__name">{m.name}</span>
                  {m.address && <span className="srch-row__addr">{m.address}</span>}
                </button>
              ))}

              {showNoMatches && (
                <p className="muted srch-note">
                  No matches. Try adding a city to narrow it down.
                </p>
              )}
            </div>
          )}

          {!typed && (
            <p className="muted" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
              Search for a local rival by name. We start tracking right away.
              Review counts and ratings fill in on the next snapshot.
            </p>
          )}

          {error && (
            <p style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>
              Could not add that business. Please try again.
            </p>
          )}
        </div>
        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ---- one comparison row (desktop grid / mobile card, via CSS) ---- */
function CompRow({
  row,
  newCount,
  reviewsMax,
}: {
  row: LeaderboardRow;
  newCount: number | null;
  reviewsMax: number;
}) {
  const label = row.label || 'Competitor';
  const rating = row.avg_rating != null && row.avg_rating > 0 ? row.avg_rating.toFixed(1) : null;
  const reviews = row.total_reviews;
  const barWidth = reviewsMax > 0 ? Math.max(2, Math.round((reviews / reviewsMax) * 100)) : 0;
  const barColor = row.is_client ? '#1a73e8' : '#c8ccd1';

  return (
    <div className={'cp-row' + (row.is_client ? ' cp-row--me' : '')}>
      {/* BUSINESS */}
      <div className="cp-biz">
        <span className="cp-biz__ava" style={{ background: avatarColor(label + row.rank) }}>
          {initialOf(label)}
        </span>
        <div className="cp-biz__meta">
          <div className="cp-biz__name">
            <b>{label}</b>
            {row.is_client && <span className="cp-you">YOU</span>}
          </div>
          {/* mobile-only sub-line folds reviews + new into one row */}
          <div className="cp-biz__sub">
            {reviews > 0 ? reviews.toLocaleString() : '0'} reviews
            {newCount != null && newCount > 0 ? ` ${MIDDOT} +${newCount} this month` : ''}
          </div>
        </div>
      </div>

      {/* RATING */}
      <div className="cp-rating">
        {rating ? (
          <>
            <span className="cp-rating__num">{rating}</span>
            <span className="cp-rating__star">{STAR}</span>
          </>
        ) : (
          <span className="cp-rating--none">{DASH}</span>
        )}
      </div>

      {/* TOTAL REVIEWS (bar + value) */}
      <div className="cp-reviews">
        <div className="cp-reviews__bar">
          <div className="cp-reviews__fill" style={{ width: barWidth + '%', background: barColor }} />
        </div>
        <span className="cp-reviews__val">{reviews > 0 ? reviews.toLocaleString() : '0'}</span>
      </div>

      {/* LAST 30 DAYS (new_in_period from the tracked list; dash when none) */}
      {newCount != null && newCount > 0 ? (
        <div className="cp-recent">+{newCount} new</div>
      ) : (
        <div className="cp-recent cp-recent--none">{DASH}</div>
      )}
    </div>
  );
}

export function Competitors() {
  const q = useCompetitors();
  const sc = useCompetitorScorecard();
  const mut = useCompetitorMutations();
  const [adding, setAdding] = useState(false);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tracked: Competitor[] = q.data?.competitors ?? [];
  const leaderboard = sc.data?.leaderboard;
  const rows: LeaderboardRow[] = leaderboard?.rows ?? [];

  function flashToast(msg: string) {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  async function handleRefresh() {
    setRefreshNote(null);
    try {
      const res = await mut.refresh();
      if (res.degraded === 'places_unconfigured') {
        setRefreshNote('Live refresh is not available right now. Showing last-known numbers.');
      } else if (res.errors.length > 0) {
        setRefreshNote(
          `Refreshed ${res.refreshed}. ${res.errors.length} could not be refreshed and kept their last-known numbers.`,
        );
      } else {
        setRefreshNote(`Refreshed ${res.refreshed} competitor${res.refreshed === 1 ? '' : 's'} from Google.`);
      }
    } catch {
      setRefreshNote('Could not refresh just now. Please try again in a moment.');
    }
  }

  async function handleAdd(match: PlaceMatch) {
    try {
      await mut.add({ competitor_label: match.name, competitor_place_id: match.place_id });
      setAdding(false);
      flashToast(`Now tracking ${match.name}.`);
    } catch {
      /* surfaced in the modal */
    }
  }

  /* --- honest derived figures for the callout + insight --- */
  const clientRow = rows.find((r) => r.is_client) ?? null;
  const rivalRows = rows.filter((r) => !r.is_client);

  // Rating rank: sort by avg_rating desc (the API leaderboard is by review COUNT).
  const rated = rows.filter((r) => r.avg_rating != null && r.avg_rating > 0);
  let yourRatingRank: number | null = null;
  if (clientRow && clientRow.avg_rating != null && clientRow.avg_rating > 0) {
    const better = rated.filter((r) => (r.avg_rating ?? 0) > (clientRow.avg_rating ?? 0)).length;
    yourRatingRank = better + 1;
  }
  const fieldSize = leaderboard?.field_size ?? rows.length;

  const yourRating =
    clientRow && clientRow.avg_rating != null && clientRow.avg_rating > 0
      ? clientRow.avg_rating.toFixed(1)
      : null;

  // Area average = mean of RIVALS' real ratings (honest; hidden when none).
  const ratedRivals = rivalRows.filter((r) => r.avg_rating != null && r.avg_rating > 0);
  const areaAvg =
    ratedRivals.length > 0
      ? (ratedRivals.reduce((s, r) => s + (r.avg_rating ?? 0), 0) / ratedRivals.length).toFixed(1)
      : null;

  const nearTop =
    yourRatingRank != null && (yourRatingRank === 1 || yourRatingRank <= Math.ceil(fieldSize / 3));

  const gap = leaderboard?.gap_to_leader ?? 0;
  const leading = gap <= 0 && rows.length > 0;

  const reviewsMax = rows.reduce((m, r) => Math.max(m, r.total_reviews), 0);

  // new_in_period from the tracked list, keyed by the opaque place_id.
  const newByPlace = new Map<string, number>();
  for (const c of tracked) newByPlace.set(c.competitor_place_id, c.new_in_period ?? 0);
  const newFor = (r: LeaderboardRow): number | null =>
    r.is_client ? null : (newByPlace.get(r.competitor_place_id) ?? 0);

  const loading = sc.isLoading || q.isLoading;
  const hasRivals = rivalRows.length > 0;

  return (
    <div className="cp">
      {/* ---- sticky full-bleed header ---- */}
      <div className="cp-head">
        <div>
          <h1>Competitors</h1>
          <p className="cp-head__sub">How you stack up against nearby rivals on Google.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {tracked.length > 0 && (
            <button
              className="cp-track"
              onClick={handleRefresh}
              disabled={mut.isRefreshing}
              title="Pull current ratings and review counts from Google"
            >
              {mut.isRefreshing ? 'Refreshing...' : 'Refresh from Google'}
            </button>
          )}
          <button className="cp-track" onClick={() => setAdding(true)}>
            <Icon d={PlusPath} sw={2.5} />
            Track a competitor
          </button>
        </div>
      </div>

      <div className="cp-body">
        {refreshNote && (
          <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
            {refreshNote}
          </p>
        )}

        {loading ? (
          <div className="cp-state">Loading your competitor standings...</div>
        ) : !hasRivals ? (
          <div className="cp-state">
            No competitors tracked yet. Use <strong>Track a competitor</strong> to add a local
            rival by name and see how you stack up. Ratings and review counts fill in on the
            next snapshot.
          </div>
        ) : (
          <>
            {/* ---- POSITION CALLOUT (3-up desktop / 2-up mobile) ---- */}
            <div className="cp-callout">
              <div className="cp-stat">
                <div className="cp-stat__lab">YOUR RATING RANK</div>
                <div className="cp-stat__row">
                  <span className="cp-stat__big">
                    {yourRatingRank != null ? `#${yourRatingRank}` : DASH}
                  </span>
                  <span className="cp-stat__of">of {fieldSize} tracked</span>
                </div>
                {yourRatingRank != null && nearTop && (
                  <div className="cp-stat__note cp-stat__note--good">You're near the top on rating</div>
                )}
              </div>

              <div className="cp-stat">
                <div className="cp-stat__lab">YOUR RATING</div>
                <div className="cp-stat__big" style={{ marginTop: 6 }}>
                  {yourRating ?? DASH}
                </div>
                {areaAvg && <div className="cp-stat__note">Area average is {areaAvg}</div>}
              </div>

              <div className="cp-stat cp-stat--gain">
                <div className="cp-stat__lab">WHERE TO GAIN</div>
                {leading ? (
                  <>
                    <div className="cp-stat__gain">Hold the lead</div>
                    <div className="cp-stat__note">You're #1 on review volume. Keep requests running.</div>
                  </>
                ) : (
                  <>
                    <div className="cp-stat__gain">Review volume</div>
                    <div className="cp-stat__note">
                      {gap > 0
                        ? `${gap.toLocaleString()} review${gap === 1 ? '' : 's'} behind the leader`
                        : 'Keep requests running to climb'}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ---- COMPARISON TABLE ---- */}
            <div className="cp-table">
              <div className="cp-table__head">
                <div>BUSINESS</div>
                <div>RATING</div>
                <div>TOTAL REVIEWS</div>
                <div>LAST 30 DAYS</div>
              </div>
              {rows.map((r) => (
                <CompRow
                  key={(r.is_client ? 'client' : r.competitor_place_id) + '-' + r.rank}
                  row={r}
                  newCount={newFor(r)}
                  reviewsMax={reviewsMax}
                />
              ))}
            </div>

            {/* ---- INSIGHT CALLOUT ---- */}
            <div className="cp-insight">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth={2}>
                {InfoCircle}
              </svg>
              <div className="cp-insight__txt">
                {yourRating && areaAvg && Number(yourRating) >= Number(areaAvg) ? (
                  <>
                    Your <strong>{yourRating} rating beats the area average</strong> of {areaAvg}
                    {gap > 0
                      ? ` ${EMDASH} your gap is review count. Keep requests running: even 3${DASH}4 more reviews a month closes the distance on the higher-volume shops fast.`
                      : '. Keep requests running to hold your lead.'}
                  </>
                ) : gap > 0 ? (
                  <>
                    You're <strong>{gap.toLocaleString()} review{gap === 1 ? '' : 's'} behind the
                    leader</strong>. Keep requests running: even 3{DASH}4 more reviews a month closes
                    the distance on the higher-volume shops fast.
                  </>
                ) : (
                  <>Keep review requests running to climb the rankings and stay ahead of nearby rivals.</>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {adding && (
        <AddCompetitorModal onClose={() => setAdding(false)} onAdd={handleAdd} saving={mut.isPending} error={mut.error} />
      )}

      {toast && (
        <div className="celebrate-toast" role="status">
          <span className="celebrate-emoji">{CHECK}</span>
          <span className="celebrate-msg">{toast}</span>
        </div>
      )}
    </div>
  );
}
